


import { CreateOrderNewResponse, Discont, DiscontStatus, ServiceHM } from '@project/shared-types';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { ServiceHMAPI } from '../../../api/base/ServiceHMAPI';
import { sendMetrics } from '../../../shared/hooks/sendMetrics';
import { useLocalStorage } from '../../../shared/hooks/useLocalStorage';
import { getDiscontsAPI } from '../../discount/model/discont.api';
import { useTokenLogin } from '../../user/hooks/useTokenLogin';
import { useUser } from "../../user/hooks/useUser";
import { OrderServiceFront } from '../model/order.service';

// Тип данных заказа (можно расширить по необходимости) 
export interface OrderPrice {
    currentPrice: number;
    prices: number[];
    activeStepIdx: number;
    nextPrice: number;
    nextStep: number;
    leftToNext: number;
    minCount: number;
    maxCount: number;
    activeStep: number;
}
// Тип контекста
interface OrderContextType {
    service: ServiceHM | null;
    isLoading: boolean;
    form: OrderContextForm;

    price: OrderPrice;
    disconts: Discont[];
    setDisconts: React.Dispatch<React.SetStateAction<Discont[]>>;
    handleCreateOrder: () => Promise<void>;
}
interface OrderContextForm {
    count: number;
    setCount: (count: number) => void;
    //Url
    url: string;
    setUrl: (url: string) => void;
    urlError: string | undefined;
    //Email
    email: string;
    setEmail: (email: string) => void;
    emailError: string | undefined;
    //Description
    description: string;
    setDescription: (description: string) => void;
}
const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider = ({ children }: { children: ReactNode }) => {
    const user = useUser()
    const [count, setCount] = useState(5);
    const [url, setUrl] = useLocalStorage("url", "");
    const [urlError, setUrlError] = useState<string | undefined>(undefined);
    const [service, setService] = useState<ServiceHM | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [emailLocal, setEmailLocal] = useLocalStorage("email", "");
    const [email, setEmail] = useState(user?.email || emailLocal);
    const [emailError, setEmailError] = useState<string | undefined>(undefined);
    const { login } = useTokenLogin();
    const [description, setDescription] = useState("");
    const [globalId] = useState<string>(
        typeof window !== "undefined"
            ? localStorage.getItem("global_id") || uuidv4()
            : uuidv4()
    );

    const [disconts, setDisconts] = useState<Discont[]>([]);
    const navigate = useNavigate();

    const getDisconts = async () => {
        try {
            const response = await getDiscontsAPI();

            if (response.disconts) {
                setDisconts(response.disconts);
            }
        } catch (error) {
            console.error("Ошибка при получении карточек услуг:", error);
        }
    };
    const location = useLocation();

    const getService = async () => {
        setIsLoading(true);
        try {
            const response = await ServiceHMAPI({
                url: location.pathname,
            });
            if (response.service) {
                setService(response.service);
                setCount(response.service.form_order.count_default);
            }
        } catch (error) {
            console.error('Ошибка при загрузке услуги:', error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getService();
        getDisconts();
    }, [location.pathname]);

    const {
        steps,
        price_min,
        price_max,
    } = useMemo(() => {
        return {
            steps: service?.steps || [],
            price_min: service?.price_min || 0,
            price_max: service?.price_max || 0,
        }
    }, [service]);

    const activeStepIdx = OrderServiceFront.getActiveStepIndex(steps, count);
    const prices = steps.map((_, idx) => OrderServiceFront.getStepPrice(steps, price_max, price_min, idx));

    // Текущая цена
    const currentPrice = useMemo(() => {
        const price = OrderServiceFront.getStepPrice(steps, price_max, price_min, activeStepIdx);
        return price;
    }, [activeStepIdx, steps, price_min, price_max]);

    // Прогресс-бар: от минимального до максимального шага
    const minCount = steps[0];
    const maxCount = 100;

    // Подсказка: сколько осталось до следующего шага и какая будет цена
    const nextStep = steps[activeStepIdx + 1];
    const nextPrice = prices[activeStepIdx + 1];
    const leftToNext = nextStep ? nextStep - count : 0;

    const price: OrderPrice = {
        currentPrice,
        prices,
        activeStepIdx,
        nextPrice,
        nextStep,
        leftToNext,
        minCount,
        maxCount,
        activeStep: activeStepIdx,
    }

    const handleCreateOrder = async () => {
        if (!service?.id) {
            return;
        }

        const discont = disconts.find((d) => d.status === DiscontStatus.OK);
        const isValidSum = OrderServiceFront.isValidSum(price.currentPrice, discont, count);
        if (!isValidSum) {
            return;
        }
        OrderServiceFront.createOrder({
            service_hm_id: service?.id,
            email: email,
            count: count,
            url: url,
            discont: discont,
            global_id: globalId,
            description: description,
        }, {
            setErrorEmail: (error: string) => {
                setEmailError(error);
            },
            setErrorUrl: (error: string) => {
                setUrlError(error);
            },
            setIsLoadingOrder: (isLoading: boolean) => {
                setIsLoading(isLoading);
            },
            handleSuccses: (response: CreateOrderNewResponse) => {
                const isRedirectEnabled = true;
                login(response.token, true);
                if (isRedirectEnabled) {
                    // Создаем временную ссылку и программно кликаем по ней
                    // Это обходит ограничения Safari на window.open()
                    const tempLink = document.createElement('a');
                    tempLink.href = response.url;
                    tempLink.target = '_blank';
                    tempLink.rel = 'noopener noreferrer';
                    tempLink.style.display = 'none';
                    document.body.appendChild(tempLink);

                    // Программно кликаем по ссылке
                    tempLink.click();

                    // Удаляем временную ссылку
                    document.body.removeChild(tempLink);

                    // Перенаправляем текущую страницу
                    navigate(`/account/order/${response.order_id}/`);
                } else {
                    navigate(url);
                }
            },
        });
    }
    const changeUrl = (url: string) => {
        setUrl(url);
        setUrlError(undefined);
    }
    const changeEmail = (email: string) => {
        setEmail(email);
        setEmailLocal(email);
        setEmailError(undefined);
    }
    const changeSetCount = (count: number) => {
        setCount(count);
        sendMetrics("reachGoal", "cheackPrice");
    }
    return (
        <OrderContext.Provider value={{
            form: {
                count, setCount: changeSetCount,
                url, setUrl: changeUrl,
                email, setEmail: changeEmail,
                description, setDescription,
                urlError,
                emailError,
            },
            service,
            isLoading,
            price,
            disconts,
            setDisconts,
            handleCreateOrder,
        }}>
            {children}
        </OrderContext.Provider>
    );
};

// Кастомный хук для использования контекста заказа
export const useOrder = () => {
    const context = useContext(OrderContext);
    if (!context) {
        throw new Error("useOrder должен использоваться внутри OrderProvider");
    }
    return context;
};